import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import { RequireRole } from "@/components/layout/DashboardLayout";
import Home from "@/pages/Home";
import { Empty } from "@/components/JobCard";
import { Seo } from "@/components/Seo";
import { Analytics } from "@/components/Analytics";

const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const info = () => import("@/pages/InfoPages");
const [Companies, CompanyDetail, CategoriesIndex, ForEmployers, ForColleges, PricingPage, About, FaqPage, Legal] = ["Companies", "CompanyDetail", "CategoriesIndex", "ForEmployers", "ForColleges", "PricingPage", "About", "FaqPage", "Legal"].map((n) => named(info, n));
const res = () => import("@/pages/CareerResources");
const [ResourcesIndex, ResourceArticle] = ["ResourcesIndex", "ResourceArticle"].map((n) => named(res, n));
const sal = () => import("@/pages/Salaries");
const [SalariesIndex, SalaryRole, SalaryRoleCity] = ["SalariesIndex", "SalaryRole", "SalaryRoleCity"].map((n) => named(sal, n));
const refs = () => import("@/pages/Referrals");
const [ReferralsPage, Leaderboard] = ["ReferralsPage", "Leaderboard"].map((n) => named(refs, n));
const auth = () => import("@/pages/Auth");
const [Login, Signup, ForgotPassword, ResetPassword, VerifyEmail] = ["Login", "Signup", "ForgotPassword", "ResetPassword", "VerifyEmail"].map((n) => named(auth, n));
const sk = () => import("@/pages/SeekerPages");
const [SeekerDashboard, Profile, Applications, SavedJobs, Alerts, Membership] = ["SeekerDashboard", "Profile", "Applications", "SavedJobs", "Alerts", "Membership"].map((n) => named(sk, n));
const em = () => import("@/pages/EmployerPages");
const [EmployerDashboard, EmployerJobs, EmployerApplications, EmployerBilling] = ["EmployerDashboard", "EmployerJobs", "EmployerApplications", "EmployerBilling"].map((n) => named(em, n));
const co = () => import("@/pages/CollegePages");
const [CollegeDashboard, Students, Drives, Placements] = ["CollegeDashboard", "Students", "Drives", "Placements"].map((n) => named(co, n));
const ad = () => import("@/pages/AdminPages");
const [AdminOverview, AdminUsers, AdminVerifications, AdminModeration, AdminReports, AdminCategories, AdminPricing, AdminPayments, AdminAudit, AdminEmails, AdminDataExport, AdminSalaries, AdminReferrals, AdminSettings] =
  ["AdminOverview", "AdminUsers", "AdminVerifications", "AdminModeration", "AdminReports", "AdminCategories", "AdminPricing", "AdminPayments", "AdminAudit", "AdminEmails", "AdminDataExport", "AdminSalaries", "AdminReferrals", "AdminSettings"].map((n) => named(ad, n));
const Fallback = () => <div className="min-h-[60vh]" data-testid="route-loading" />;

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const NotFound = () => <div className="container-x py-20"><Seo title="Page not found" noindex /><Empty title="Page not found" text="The page you're looking for doesn't exist." /></div>;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollTop />
        <Toaster richColors position="top-center" />
        <Analytics />
        <Suspense fallback={<Fallback />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:slug" element={<JobDetail />} />
            <Route path="/categories/:slug" element={<Jobs />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:slug" element={<CompanyDetail />} />
            <Route path="/categories" element={<CategoriesIndex />} />
            <Route path="/career-resources" element={<ResourcesIndex />} />
            <Route path="/career-resources/:slug" element={<ResourceArticle />} />
            <Route path="/salaries" element={<SalariesIndex />} />
            <Route path="/salaries/:role" element={<SalaryRole />} />
            <Route path="/salaries/:role/:city" element={<SalaryRoleCity />} />
            <Route path="/referral-leaderboard" element={<Leaderboard />} />
            <Route path="/for-employers" element={<ForEmployers />} />
            <Route path="/for-colleges" element={<ForColleges />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<RequireRole roles={["seeker"]} />}>
            <Route path="/dashboard" element={<SeekerDashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/applications" element={<Applications />} />
            <Route path="/saved-jobs" element={<SavedJobs />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/referrals" element={<ReferralsPage />} />
          </Route>
          <Route element={<RequireRole roles={["employer"]} />}>
            <Route path="/employer/dashboard" element={<EmployerDashboard />} />
            <Route path="/employer/jobs" element={<EmployerJobs />} />
            <Route path="/employer/applications" element={<EmployerApplications />} />
            <Route path="/employer/billing" element={<EmployerBilling />} />
            <Route path="/employer/referrals" element={<ReferralsPage />} />
          </Route>
          <Route element={<RequireRole roles={["college"]} />}>
            <Route path="/college/dashboard" element={<CollegeDashboard />} />
            <Route path="/college/students" element={<Students />} />
            <Route path="/college/drives" element={<Drives />} />
            <Route path="/college/placements" element={<Placements />} />
          </Route>
          <Route element={<RequireRole roles={["admin"]} />}>
            <Route path="/admin" element={<AdminOverview />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/data" element={<AdminDataExport />} />
            <Route path="/admin/verifications" element={<AdminVerifications />} />
            <Route path="/admin/moderation" element={<AdminModeration />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/pricing" element={<AdminPricing />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/audit" element={<AdminAudit />} />
            <Route path="/admin/emails" element={<AdminEmails />} />
            <Route path="/admin/salaries" element={<AdminSalaries />} />
            <Route path="/admin/referrals" element={<AdminReferrals />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
          </Route>
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
